import React, { useCallback, useEffect, useRef, useState } from "react";
import "../../assets/styles/preTreatmentReport.scss";
import rcIcon from "../../assets/images/image 1.png";
import { convertToNameLabel, mask, wrapList } from "../../utils/utils";
import { commonPostApi } from "../../utils/apiService";
import { API_ENDPOINTS } from "../../utils/apiEndpoints";
import ProfileView from '../../components/common/profileView';
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useReactToPrint } from "react-to-print";
import TenanReportPrint from "./TenantReportPrint";

export default function TenantPatientVisitReport() {
  const { id } = useParams();
  const campIdFromSettings = useSelector((state) => state.settings.camp?.camp_details_id);
  const campIdFromCommon = useSelector((state) => state.common.camp?.camp_details_id);
  const campId = campIdFromSettings || campIdFromCommon || localStorage.getItem("camp_id");
  const tenantId = useSelector((state) => state.common.organization?.tenant_id);

  const [treatment, setTreatment] = useState({});
  const [patient, setPatient] = useState({});
  const [profileLoading, setProfileLoading] = useState({});
  const [tenantReportData,setTenantReportData]=useState([])
  const printRef=useRef(null);
  // Eye examination data
  const [examinationDetails, setExaminationDetails] = useState([
    { label: "Visual Acuity", LDO: "123", RDO: "123", api_key: "va" },
    { label: "Intraocular Pressure", LDO: "123", RDO: "123", api_key: "iop" },
    { label: "Auto Refraction", LDO: "123", RDO: "123", api_key: "auto_refraction" },
    { label: "Dilated Refraction", LDO: "123", RDO: "123", api_key: "refraction_dilated" },
    { label: "PGP", LDO: "123", RDO: "123", api_key: "pgp" },
    { label: "Glasses Prescription", LDO: "123", RDO: "123", api_key: "glasses_prescription" },
    { label: "Intermediate Glasses Prescription", LDO: "123", RDO: "123", api_key: "intermediate_glasses_prescription" },
    { label: "PMT", LDO: "123", RDO: "123", api_key: "pmt" },
    { label: "Retinoscopy", LDO: "123", RDO: "123", api_key: "retinoscopy" },
    { label: "Keratometry", LDO: "123", RDO: "123", api_key: "keratometry" },
    { label: "Amsler", LDO: "123", RDO: "123", api_key: "amsler" },
    { label: "Contact Lens Prescription", LDO: "123", RDO: "123", api_key: "contact_lens_prescription" },
    { label: "Color Vision", LDO: "123", RDO: "123", api_key: "color_vision" },
    { label: "Contrast Sensitivity", LDO: "123", RDO: "123", api_key: "contrast_sensitivity" },
    { label: "Orthoptics", LDO: "123", RDO: "123", api_key: "orthoptics" },
    { label: "Other", LDO: "123", RDO: "123", api_key: "others" },
  ])

  // Helper function to get visit data by visit name
  const getVisitData = (visitName) => {
    const visits = treatment?.finalData || [];
    return visits.find(visit => visit.name === visitName) || {};
  }

  // Helper function to format history data for display
  const formatHistoryData = (historyArray, visitName) => {
    const filtered = (historyArray || []).filter(item => item.visit_name === visitName);
    return filtered.map(item => item.history_name).join(", ") || "-";
  }

  // Helper function to get clinical data from visit
  const getClinicalData = (visitData) => {
    const clinical = visitData?.patientvisitclinical?.[0] || {};
    return {
      procedures: clinical.procedures || "-",
      immunizations: clinical.immunizations || "-",
      medications: clinical.medications || "-",
      additional_notes: clinical.additional_notes || "-",
      ophthalmic_history: formatHistoryData(treatment?.ophthalmic_history, visitData?.name),
      allergies: formatHistoryData(treatment?.allergies, visitData?.name),
      medical_history: formatHistoryData(treatment?.medical_history, visitData?.name),
      family_history: formatHistoryData(treatment?.family_history, visitData?.name),
    };
  }


  // Helper function to get examination details for a visit
  const getExaminationDetailsForVisit = (visitData) => {
    const [leftEye] = visitData?.patientvisiteyeexam?.filter(item => item?.eye_type === "Left Eye") ?? []
    const [rightEye] = visitData?.patientvisiteyeexam?.filter(item => item?.eye_type === "Right Eye") ?? []
    
    return [
      { label: "Visual Acuity", LDO: leftEye?.va ?? "-", RDO: rightEye?.va ?? "-", api_key: "va" },
      { label: "Intraocular Pressure", LDO: leftEye?.iop ?? "-", RDO: rightEye?.iop ?? "-", api_key: "iop" },
      { label: "Auto Refraction", LDO: leftEye?.auto_refraction ?? "-", RDO: rightEye?.auto_refraction ?? "-", api_key: "auto_refraction" },
      { label: "Dilated Refraction", LDO: leftEye?.refraction_dilated ?? "-", RDO: rightEye?.refraction_dilated ?? "-", api_key: "refraction_dilated" },
      { label: "PGP", LDO: leftEye?.pgp ?? "-", RDO: rightEye?.pgp ?? "-", api_key: "pgp" },
      { label: "Glasses Prescription", LDO: leftEye?.glasses_prescription == null ? "-" : (leftEye?.glasses_prescription ? "Yes" : "No"), RDO: rightEye?.glasses_prescription == null ? "-" : (rightEye?.glasses_prescription ? "Yes" : "No"), api_key: "glasses_prescription" },
      { label: "Intermediate Glasses Prescription", LDO: leftEye?.intermediate_glasses_prescription ?? "-", RDO: rightEye?.intermediate_glasses_prescription ?? "-", api_key: "intermediate_glasses_prescription" },
      { label: "PMT", LDO: leftEye?.pmt ?? "-", RDO: rightEye?.pmt ?? "-", api_key: "pmt" },
      { label: "Retinoscopy", LDO: leftEye?.retinoscopy ?? "-", RDO: rightEye?.retinoscopy ?? "-", api_key: "retinoscopy" },
      { label: "Keratometry", LDO: leftEye?.keratometry ?? "-", RDO: rightEye?.keratometry ?? "-", api_key: "keratometry" },
      { label: "Amsler", LDO: leftEye?.amsler ?? "-", RDO: rightEye?.amsler ?? "-", api_key: "amsler" },
      { label: "Contact Lens Prescription", LDO: leftEye?.contact_lens_prescription ?? "-", RDO: rightEye?.contact_lens_prescription ?? "-", api_key: "contact_lens_prescription" },
      { label: "Color Vision", LDO: leftEye?.color_vision ?? "-", RDO: rightEye?.color_vision ?? "-", api_key: "color_vision" },
      { label: "Contrast Sensitivity", LDO: leftEye?.contrast_sensitivity ?? "-", RDO: rightEye?.contrast_sensitivity ?? "-", api_key: "contrast_sensitivity" },
      { label: "Orthoptics", LDO: leftEye?.orthoptics ?? "-", RDO: rightEye?.orthoptics ?? "-", api_key: "orthoptics" },
      { label: "Other", LDO: leftEye?.others ?? "-", RDO: rightEye?.others ?? "-", api_key: "others" },
    ];
  }

  const renderMedicalPrescriptionSection = (visitData) => {
    const prescriptions = visitData?.medicalprescription ?? [];

    return (
      <div className="pt-4">
        <div className="hidden">
          <div ref={printRef}>{<TenanReportPrint tenantReportData={tenantReportData} />}</div>
        </div>
        <div className="parameter">Medical Prescription</div>

        {prescriptions.length === 0 && (
          <div className="medical-row pt-2 pb-4">
            <div className="vital-item">
              <div className="mark">Name</div>
              <div className="brief">-</div>
            </div>
            <div className="vital-item text-center">
              <div className="mark">Dosage</div>
              <div className="brief">-</div>
            </div>
            <div className="vital-item text-center">
              <div className="mark">Type</div>
              <div className="brief">-</div>
            </div>
            <div className="vital-item md:text-center">
              <div className="mark">Frequency</div>
              <div className="brief">-</div>
            </div>
            <div className="vital-item text-center">
              <div className="mark">Route</div>
              <div className="brief">-</div>
            </div>
            <div className="vital-item text-center md:text-right">
              <div className="mark">Period</div>
              <div className="brief">-</div>
            </div>
          </div>
        )}

        {prescriptions.length > 0 &&
          prescriptions.map((prescription, index) => (
            <div
              className="medical-row pt-2 pb-4"
              key={prescription?.medical_prescription_id || index}
            >
              <div className="vital-item">
                <div className="mark">Name</div>
                <div className="brief">
                  {prescription?.medicineLookup?.name ?? "-"}
                </div>
              </div>
              <div className="vital-item text-center">
                <div className="mark">Dosage</div>
                <div className="brief">
                  {prescription?.dosageLookup?.name ?? "-"}
                </div>
              </div>
              <div className="vital-item text-center">
                <div className="mark">Type</div>
                <div className="brief">{prescription?.typeLookup?.name ?? "-"}</div>
              </div>
              <div className="vital-item md:text-center">
                <div className="mark">Frequency</div>
                <div className="brief">
                  {prescription?.frequencyLookup?.name ?? "-"}
                </div>
              </div>
              <div className="vital-item text-center">
                <div className="mark">Route</div>
                <div className="brief">{prescription?.routeLookup?.name ?? "-"}</div>
              </div>
              <div className="vital-item text-center md:text-right">
                <div className="mark">Period</div>
                <div className="brief">{prescription?.periodLookup?.name ?? "-"}</div>
              </div>
            </div>
          ))}
      </div>
    );
  };

  const init = useCallback(async () => {

    try {
      const { data = {} } = await commonPostApi(API_ENDPOINTS.SEARCH_PATIENT_DETAILS, {
        patient_id: id,
      });

      const patientDetails = data?.patient;
      patientDetails.aarogyasri_no = patientDetails["health_insurance_card"];
      patientDetails.aadhaar_no = patientDetails["aadhar_no"];
      const finalList = {};
      [
        "patient_code",
        "gender",
        "dob",
        "aadhaar_no",
        "aarogyasri_no",
        "ration_card_no",
        "address",
      ].forEach((item) => {
        if (item === "patient_code") {
          finalList["patient_iD"] = patientDetails[item];
        } else {
          finalList[item] = patientDetails[item];
        }
      });

      let finalData = convertToNameLabel(finalList, [
        "patient_name",
        "first_name",
        "last_name",
        "profile_picture",
        "district",
        "state",
        "city",
        "pincode",
        "phone_no",
        "updated_by",
        "created_at",
        "created_by",
        "updated_at",
      ]);
      finalData.name = patientDetails["patient_name"];
      finalData.imgUrl = patientDetails?.profile_picture
        ? `${process.env?.REACT_APP_CURRENT_URL}${API_ENDPOINTS.SEARCH_IMAGE_BY_ID(patientDetails?.patient_id)}`
        : null;
      finalData = Object.entries(finalData)
        .map(([label, value]) => {
          if (
            value?.value &&
            ["aadhaar_no", "aarogyasri_no", "ration_card_no"].includes(label)
          ) {
            return { [label]: { ...value, value: mask(value?.value) } };
          } else {
            return { [label]: value };
          }
        })
        ?.reduce((acc, item) => {
          const normalizedItem =
            Array.isArray(item) && item.length === 1 ? item[0] : item;
          return { ...acc, ...normalizedItem };
        }, {});

      setPatient({ finalData, originalData: patientDetails });
      setProfileLoading(false);
    } catch (err) {
      console.log(err);
      setProfileLoading(false);
    }
  }, [id])

  useEffect(() => {
    if (!id || !campId || !tenantId) {
      return; 
    }
    
    init()
    commonPostApi(API_ENDPOINTS.PATIENT_POSTTREATMENT_REPORT, {
      "patient_id": id,
      "camp_details_id": campId,
      "tenant_id": tenantId,
    }).then(data => {
      const payload = data?.data?.data ?? data?.data ?? {};
      console.log("payload", payload);
      setTreatment(payload)
    }).catch(error => {
      console.error("Error fetching treatment report:", error);
    })
  }, [id, campId, tenantId, init]
  )

  const initialVisit = getVisitData("1st (New)");
  const seventhDayVisit = getVisitData("7th (Return)");
  const fourteenthDayVisit = getVisitData("14th (Return)");
  const fortiethDayVisit = getVisitData("40th (Return)");

  // Get eye examination data for initial visit
  const [left_eye] = initialVisit?.patientvisiteyeexam?.filter(item => item?.eye_type === "Left Eye") ?? []
  const [right_eye] = initialVisit?.patientvisiteyeexam?.filter(item => item?.eye_type === "Right Eye") ?? []

  useEffect(() => {
    const [leftEye] = initialVisit?.patientvisiteyeexam?.filter(item => item?.eye_type === "Left Eye") ?? []
    const [rightEye] = initialVisit?.patientvisiteyeexam?.filter(item => item?.eye_type === "Right Eye") ?? []

    setExaminationDetails(prev => {
      return prev?.map(
        item => ({ ...item, LDO: leftEye?.[item?.api_key] ?? "-", RDO: rightEye?.[item?.api_key] ?? "-" })
      )
    })
  }, [treatment, initialVisit])

     const handlePrint = useReactToPrint({
       contentRef: printRef,
       onBeforeGetContent: () => {
         console.log("Before print - TenantReport data:", tenantReportData);
         if (!tenantReportData || !printRef.current) {
           console.log("Print cancelled - missing data or ref");
           return Promise.reject("Missing data or ref");
         }
         return Promise.resolve();
       },
       onAfterPrint: () => {
         setTenantReportData(null);
       },
       onPrintError: (error) => {
         console.error("Print error:", error);
       },
     });
const fetchData = async () => {
  try {
    const response = await commonPostApi(
      API_ENDPOINTS.PATIENT_POSTTREATMENT_REPORT,
      {
        patient_id: id,
        camp_details_id: campId,
        tenant_id: tenantId,
      },
    );

    const reportData = response?.data;

    if (!reportData) {
      console.error("No report data");
      return;
    }
    setTenantReportData(reportData);

    setTimeout(() => {
      if (printRef.current) {
        handlePrint();
      }
    }, 1000);
  } catch (error) {
    console.error("Error fetching report:", error);
  }
};


  return (
    <div>
      <ProfileView
        profile={patient?.finalData}
        loading={profileLoading}
        parentClass={"ml-auto"}
      />


      <div className="tenant-buttons">
        <button className="btn btn-primary">
          <i className="pi pi-download"></i> Download
        </button>
        <button className="btn btn-secondary" onClick={fetchData}>
          <i className="pi pi-print"></i> Print
        </button>
        <button className="btn btn-primary">
          <i className="pi pi-upload"></i> Share
        </button>
      </div>

      <div className="mobile-tenant-buttons">
          <i className="pi pi-download"></i> 
          <i className="pi pi-print" onClick={fetchData}></i> 
          <i className="pi pi-upload"></i> 
      </div>
      

      <div className="post-treatment-report Pre-Treatment-Report-print-page2">
        <div className="headline">
          <div className="flex gap-2">
            <img src={rcIcon} width="24" height="24" alt="" />
            <div>Vital Impact</div>
          </div>
        </div>
        <div className="sub p-4">
          <div className="pre-button">Patient Visit Report</div>
        </div>
        <div className="treatment-details md:pb-4">
          <div className="pre-detail-item">
            <span className="front">Camp Details</span>{" "}
            <span className="brief">{initialVisit?.camp_name ?? "-"}</span>
          </div>
          <div className="pre-detail-item">
            <span className="front">Patient name</span>{" "}
            <span className="brief">{initialVisit?.patient_name ?? "-"}</span>
          </div>
          <div className="pre-detail-item">
            <div className="front">Patient ID</div>
            <div className="brief">{initialVisit?.patient_code ?? "-"}</div>
          </div>
          <div className="re-detail-item">
            <div className="front">Patient Aadhaar Number</div>
            <div className="brief">{initialVisit?.aadhar_no ? mask(initialVisit?.aadhar_no) : "-"}</div>
          </div>
        </div>

        <div className="md:pt-4 pt-2">
          <div className="brief">Initial Visit</div>
          <div className="parameter">Patient Vitals</div>
          <div className="vitals-row pt-2 pb-4">
            <div className="vital-item">
              <div className="mark">Age</div>
              <div className="brief">{initialVisit?.age ?? "-"}</div>
            </div>
            <div className="vital-item text-center">
              <div className="mark">Height</div>
              <div className="brief">{initialVisit?.height ?? "-"}</div>
            </div>
            <div className="vital-item text-center">
              <div className="mark">Weight</div>
              <div className="brief">{initialVisit?.weight ?? "-"}</div>
            </div>
            <div className="vital-item text-left md:text-center">
              <div className="mark">pulse</div>
              <div className="brief">{initialVisit?.pulse ? `${initialVisit.pulse} bpm` : "-"}</div>
            </div>
            <div className="vital-item pl-4 md:pl-0 md:text-right">
              <div className="mark">Blood Pressure</div>
              <div className="brief">{initialVisit?.blood_pressure ? `${initialVisit.blood_pressure} mm Hg` : "-"}</div>
            </div>
          </div>
        </div>
        {(() => {
          const clinical = getClinicalData(initialVisit);
          return (
            <>
              <div className="md:pb-4">
                <div className="md:flex justify-content-between">
                  <div className="flex-1">
                    <div className="front md:pt-4">Ophthalmic History</div>
                    <span className="brief">{clinical.ophthalmic_history}</span>
                  </div>
                  <div className="md:flex-1 md:pl-4">
                    <div className="front pt-3 md:pt-4">Allergies</div>
                    <span className="brief">{clinical.allergies}</span>
                  </div>
                </div>
              </div>

              <div className="md:pb-4">
                <div className="md:flex justify-content-between">
                  <div className="flex-1">
                    <div className="front pt-3 md:pt-4">Medical History</div>
                    <span className="brief">{clinical.medical_history}</span>
                  </div>
                  <div className="flex-1 md:pl-4">
                    <div className="front pt-3 md:pt-4">Family History</div>
                    <span className="brief">{clinical.family_history}</span>
                  </div>
                </div>
              </div>

              <div className="md:pb-4">
                <div className="md:flex justify-content-between">
                  <div className="flex-1">
                    <div className="front pt-3 md:pt-4">Procedures</div>
                    <span className="brief">{clinical.procedures}</span>
                  </div>
                  <div className="flex-1 md:pl-4">
                    <div className="front pt-3 md:pt-4">Immunizations</div>
                    <span className="brief">{clinical.immunizations}</span>
                  </div>
                </div>
              </div>

              <div className="md:pb-4">
                <div className="flex justify-content-between">
                  <div className="flex-1">
                    <div className="front pt-3 md:pt-4">Medications</div>
                    <span className="brief">{clinical.medications}</span>
                  </div>
                </div>
              </div>
            </>
          );
        })()}

        <div className="dry-refraction-container">
          {/* Title */}
          <div className="front main-label pt-3">Dry Refraction</div>
        </div>

        {/* Tables Container */}
        <div className="tables-container mb-3">
          <div className="flex flex-column w-full">
            <div className="left-vision main-label text-center">L/DO</div>
            <div className="table-section">
              <table>
                <thead className="brief">
                  <tr className="brief">
                    <th className="main-label-sec font-normal font-13px">Type</th>
                    <th className="main-label-sec font-normal font-13px">Sph</th>
                    <th className="main-label-sec font-normal font-13px">Ne.</th>
                    <th className="main-label-sec font-normal font-13px">Axis</th>
                    <th className="main-label-sec font-normal font-13px">Vis</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="brief main-label-sec">Distance</td>
                    <td>{left_eye?.distance_sph ?? "-"}</td>
                    <td>{left_eye?.distance_necylar ?? "-"}</td>
                    <td>{left_eye?.distance_axis ?? "-"}</td>
                    <td>{left_eye?.distance_vision ?? "-"}</td>
                  </tr>
                  <tr>
                    <td className="brief main-label-sec">Near</td>
                    <td>{left_eye?.near_sph ?? "-"}</td>
                    <td>{left_eye?.near_necylar ?? "-"}</td>
                    <td>{left_eye?.near_axis ?? "-"}</td>
                    <td>{left_eye?.near_vision ?? "-"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Divider */}
          <div className="vertical-line "></div>

          <div className="flex flex-column w-full mt-4 md:mt-0">
            <div className="text-center main-label left-vision">R/DO</div>
            <div className="table-section">
              <table>
                <thead>
                  <tr className="main-label-sec">
                    <th className="main-label-sec font-normal font-13px">Type</th>
                    <th className="main-label-sec font-normal font-13px">Sph</th>
                    <th className="main-label-sec font-normal font-13px">Ne.</th>
                    <th className="main-label-sec font-normal font-13px">Axis</th>
                    <th className="main-label-sec font-normal font-13px">Vis</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="brief main-label-sec">Distance</td>
                    <td>{right_eye?.distance_sph ?? "-"}</td>
                    <td>{right_eye?.distance_necylar ?? "-"}</td>
                    <td>{right_eye?.distance_axis ?? "-"}</td>
                    <td>{right_eye?.distance_vision ?? "-"}</td>
                  </tr>
                  <tr>
                    <td className="brief main-label-sec">Near</td>
                    <td>{right_eye?.near_sph ?? "-"}</td>
                    <td>{right_eye?.near_necylar ?? "-"}</td>
                    <td>{right_eye?.near_sph ?? "-"}</td>
                    <td>{right_eye?.near_vision ?? "-"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className=" pt-3"></div>
        {/* Eye Examination Details Section */}
        <div className="eye-examination-details">
          <div className="front main-label md:pt-3 text-center pb-2">Eye Examination Details</div>

          <div className="tables-container">
            {/* L/DO Table */}
            <div className="table-container">
              <div className="front main-label md:pt-3 text-center">L/DO</div>
              <div className="exam-table p-1rem">
                <div>
                  {wrapList(examinationDetails).map((item, index) => (
                    <div className="" key={index}>
                      <div className="flex justify-content-between">
                        {item?.map((list, ind) => (
                          <>
                            {ind === 1 && <div className="divider my-2" />}
                            <div className="flex flex-column flex-1 pl-20px p-2">
                              <div className="main-label Outline front">{list.label}</div>
                              <div className="Benefit main-label front">{list.RDO}</div>
                            </div>
                          </>))}
                      </div>
                      <div className="Boundary"></div>

                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="standing-stroke"></div>

            {/* R/DO Table */}
            <div className="table-container">
              <div className="front main-label pt-5 md:pt-3  text-center">R/DO</div>
              <table className="exam-table p-1rem">
                <div>
                  {wrapList(examinationDetails).map((item, index) => (
                    <div className="" key={index}>
                      <div className="flex justify-content-between">
                        {item?.map((list, ind) => (
                          <>
                            {ind === 1 && <div className="divider my-2" />}
                            <div className="flex flex-column flex-1 pl-20px p-2">
                              <div className="Outline main-label front">{list.label}</div>
                              <div className="Benefit main-label front">{list.RDO}</div>
                            </div>
                          </>))}
                      </div>
                      <div className="Boundary"></div>

                    </div>
                  ))}
                </div>
              </table>
            </div>
          </div>
        </div>

        {(() => {
          const surgeryRec = initialVisit?.surgeryrecommended?.[0];
          const postSurgery = initialVisit?.patientvisitpostsurgery?.[0];
          return (
            <>
              <div className="Patient-details md:pb-4 pt-5 gap-8">
                <div className="pre-detail-item">
                  <span className="front">Patient Condition</span>{" "}
                  <span className="brief">{surgeryRec?.lookup?.name ?? "-"}</span>
                </div>
                <div className="pre-detail-item">
                  <span className="front">Treatment Recommended</span>{" "}
                  <span className="brief">{surgeryRec?.lookup?.name ?? "-"}</span>
                </div>
                <div className="pre-detail-item">
                  <div className="front">Date of Transportation</div>
                  <div className="brief">{postSurgery?.date_of_surgery ? new Date(postSurgery.date_of_surgery).toLocaleDateString() : "-"}</div>
                </div>
              </div>
              {postSurgery && (
                <div className="md:pt-4">
                  <div className="heading">Surgery</div>
                  <div className="flex pt-2 md:pb-2">
                    <div className="vital-item">
                      <div className="mark">Date</div>
                      <div className="brief">{postSurgery?.date_of_surgery ? new Date(postSurgery.date_of_surgery).toLocaleDateString() : "-"}</div>
                    </div>
                    <div className="vital-item text-center">
                      <div className="mark">Treated by</div>
                      <div className="brief">{postSurgery?.treated_by ?? "-"}</div>
                    </div>
                    <div className="vital-item text-center">
                      <div className="mark">Surgery Type</div>
                      <div className="brief">{postSurgery?.surgery_type ?? "-"}</div>
                    </div>
                    <div className="vital-item text-center">
                      <div className="mark">Lens Type</div>
                      <div className="brief">{postSurgery?.lens_type ?? "-"}</div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="front pt-1 md:pt-4">Summary</div>
                    <span className="brief">{postSurgery?.treatment_summary || postSurgery?.surgery_details}</span>
                  </div>
                  <div className="Surgery-row pb-3"></div>
                </div>
              )}
            </>
          );
        })()}

        {(() => {
          const clinical = getClinicalData(seventhDayVisit);
          // computed below where the tables render
          return (
            <>
              <div className="heading mt-3 md:mt-0">7 th Day</div>
              <div className="md:pt-4 pt-2">
                <div className="parameter">Patient Vitals</div>
                <div className="Surgery-row pt-2 pb-4">
                  <div className="vital-item">
                    <div className="mark">Age</div>
                    <div className="brief">{seventhDayVisit?.age ?? "-"}</div>
                  </div>
                  <div className="vital-item text-center">
                    <div className="mark">Height</div>
                    <div className="brief">{seventhDayVisit?.height ?? "-"}</div>
                  </div>
                  <div className="vital-item text-center">
                    <div className="mark">Weight</div>
                    <div className="brief">{seventhDayVisit?.weight ?? "-"}</div>
                  </div>
                  <div className="vital-item text-center">
                    <div className="mark">pulse</div>
                    <div className="brief">{seventhDayVisit?.pulse ? `${seventhDayVisit.pulse} bpm` : "-"}</div>
                  </div>
                  <div className="vital-item full-row mt-2 md:mt-0 md:text-right">
                    <div className="mark ">Blood Pressure</div>
                    <div className="brief">{seventhDayVisit?.blood_pressure ? `${seventhDayVisit.blood_pressure} mm Hg` : "-"}</div>
                  </div>
                </div>
              </div>

              <div className="md:pb-4">
                <div className="md:flex justify-content-between">
                  <div className="flex-1">
                    <div className="front md:pt-4">Ophthalmic History</div>
                    <span className="brief">{clinical.ophthalmic_history}</span>
                  </div>
                  <div className="flex-1 md:pl-4">
                    <div className="front pt-3 md:pt-4">Allergies</div>
                    <span className="brief">{clinical.allergies}</span>
                  </div>
                </div>
              </div>

              <div className="md:pb-4">
                <div className="md:flex justify-content-between">
                  <div className="flex-1">
                    <div className="front pt-3 md:pt-4">Medical History</div>
                    <span className="brief">{clinical.medical_history}</span>
                  </div>
                  <div className="flex-1 md:pl-4">
                    <div className="front pt-3 md:pt-4">Family History</div>
                    <span className="brief">{clinical.family_history}</span>
                  </div>
                </div>
              </div>

              <div className="md:pb-4">
                <div className="md:flex justify-content-between">
                  <div className="flex-1">
                    <div className="front pt-3 md:pt-4">Procedures</div>
                    <span className="brief">{clinical.procedures}</span>
                  </div>
                  <div className="flex-1 md:pl-4">
                    <div className="front pt-3 md:pt-4">Immunizations</div>
                    <span className="brief">{clinical.immunizations}</span>
                  </div>
                </div>
              </div>

              <div className="md:pb-4">
                <div className="flex justify-content-between">
                  <div className="flex-1">
                    <div className="front pt-3 md:pt-4">Medications</div>
                    <span className="brief">{clinical.medications}</span>
                  </div>
                </div>
              </div>
            </>
          );
        })()}

        {(() => {
          const [leftEye7th] = seventhDayVisit?.patientvisiteyeexam?.filter(item => item?.eye_type === "Left Eye") ?? []
          const [rightEye7th] = seventhDayVisit?.patientvisiteyeexam?.filter(item => item?.eye_type === "Right Eye") ?? []
          return (
            <>
              <div className="dry-refraction-container">
                {/* Title */}
                <div className="front main-label pt-3">Dry Refraction</div>
              </div>

              {/* Tables Container */}
              <div className="tables-container mb-3">
                <div className="flex flex-column w-full">
                  <div className="left-vision main-label text-center">L/DO</div>
                  <div className="table-section">
                    <table>
                      <thead className="brief">
                        <tr className="brief">
                          <th className="main-label-sec font-normal font-13px">Type</th>
                          <th className="main-label-sec font-normal font-13px">Sph</th>
                          <th className="main-label-sec font-normal font-13px">Ne.</th>
                          <th className="main-label-sec font-normal font-13px">Axis</th>
                          <th className="main-label-sec font-normal font-13px">Vis</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="brief main-label-sec">Distance</td>
                          <td>{leftEye7th?.distance_sph ?? "-"}</td>
                          <td>{leftEye7th?.distance_necylar ?? "-"}</td>
                          <td>{leftEye7th?.distance_axis ?? "-"}</td>
                          <td>{leftEye7th?.distance_vision ?? "-"}</td>
                        </tr>
                        <tr>
                          <td className="brief main-label-sec">Near</td>
                          <td>{leftEye7th?.near_sph ?? "-"}</td>
                          <td>{leftEye7th?.near_necylar ?? "-"}</td>
                          <td>{leftEye7th?.near_axis ?? "-"}</td>
                          <td>{leftEye7th?.near_vision ?? "-"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Divider */}
                <div className="vertical-line"></div>

                <div className="flex flex-column w-full mt-3 md:mt-0">
                  <div className="text-center main-label left-vision">R/DO</div>
                  <div className="table-section">
                    <table>
                      <thead>
                        <tr className="main-label-sec">
                          <th className="main-label-sec font-normal font-13px">Type</th>
                          <th className="main-label-sec font-normal font-13px">Sph</th>
                          <th className="main-label-sec font-normal font-13px">Ne.</th>
                          <th className="main-label-sec font-normal font-13px">Axis</th>
                          <th className="main-label-sec font-normal font-13px">Vis</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="brief main-label-sec">Distance</td>
                          <td>{rightEye7th?.distance_sph ?? "-"}</td>
                          <td>{rightEye7th?.distance_necylar ?? "-"}</td>
                          <td>{rightEye7th?.distance_axis ?? "-"}</td>
                          <td>{rightEye7th?.distance_vision ?? "-"}</td>
                        </tr>
                        <tr>
                          <td className="brief main-label-sec">Near</td>
                          <td>{rightEye7th?.near_sph ?? "-"}</td>
                          <td>{rightEye7th?.near_necylar ?? "-"}</td>
                          <td>{rightEye7th?.near_axis ?? "-"}</td>
                          <td>{rightEye7th?.near_vision ?? "-"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          );
        })()}

        {(() => {
          const examDetails7th = getExaminationDetailsForVisit(seventhDayVisit);
          return (
            <>
              <div className="md:pt-3 pb-2"></div>
              {/* Eye Examination Details Section */}
              <div className="eye-examination-details">
                <div className="front main-label pt-3 text-center pb-2">Eye Examination Details</div>

                <div className="tables-container">
                  {/* L/DO Table */}
                  <div className="table-container">
                    <div className="front main-label pt-3 text-center">L/DO</div>
                    <div className="exam-table p-1rem">
                      <div>
                        {wrapList(examDetails7th).map((item, index) => (
                          <div className="" key={index}>
                            <div className="flex justify-content-between">
                              {item?.map((list, ind) => (
                                <>
                            {ind === 1 && <div className="divider my-2" />}
                                  <div className="flex flex-column flex-1 pl-20px p-2">
                                    <div className="main-label Outline front">{list.label}</div>
                                    <div className="Benefit main-label front">{list.LDO}</div>
                                  </div>
                                </>))}
                            </div>
                            <div className="Boundary"></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="standing-stroke"></div>

                  {/* R/DO Table */}
                  <div className="table-container">
                    <div className="front main-label pt-3 text-center">R/DO</div>
                    <table className="exam-table p-1rem">
                      <div>
                        {wrapList(examDetails7th).map((item, index) => (
                          <div className="" key={index}>
                            <div className="flex justify-content-between">
                              {item?.map((list, ind) => (
                                <>
                                  {ind === 1 && <div className="divider my-2" />}
                                  <div className="flex flex-column flex-1 pl-20px p-2">
                                    <div className="Outline main-label front">{list.label}</div>
                                    <div className="Benefit main-label front">{list.RDO}</div>
                                  </div>
                                </>))}
                            </div>
                            <div className="Boundary"></div>
                          </div>
                        ))}
                      </div>
                    </table>
                  </div>
                </div>
              </div>
            </>
          );
        })()}

        {renderMedicalPrescriptionSection(seventhDayVisit)}

        {(() => {
          const clinical = getClinicalData(fourteenthDayVisit);
          return (
            <>
              <div className="md:pt-4">
                <div className="brief">14th Day</div>
                <div className="parameter">Patient Vitals</div>
                <div className="medical-row  pt-2 pb-4">
                  <div className="vital-item">
                    <div className="mark">Age</div>
                    <div className="brief">{fourteenthDayVisit?.age ?? "-"}</div>
                  </div>
                  <div className="vital-item text-center">
                    <div className="mark">Height</div>
                    <div className="brief">{fourteenthDayVisit?.height ?? "-"}</div>
                  </div>
                  <div className="vital-item text-center">
                    <div className="mark">Weight</div>
                    <div className="brief">{fourteenthDayVisit?.weight ?? "-"}</div>
                  </div>
                  <div className="vital-item md:text-center">
                    <div className="mark">pulse</div>
                    <div className="brief">{fourteenthDayVisit?.pulse ? `${fourteenthDayVisit.pulse} bpm` : "-"}</div>
                  </div>
                  <div className="vital-item ml-4 md:ml-0 full-row md:text-right">
                    <div className="mark">Blood Pressure</div>
                    <div className="brief">{fourteenthDayVisit?.blood_pressure ? `${fourteenthDayVisit.blood_pressure} mm Hg` : "-"}</div>
                  </div>
                </div>
              </div>

              <div className="md:pb-4">
                <div className="md:flex justify-content-between">
                  <div className="flex-1">
                    <div className="front md:pt-4">Ophthalmic History</div>
                    <span className="brief">{clinical.ophthalmic_history}</span>
                  </div>
                  <div className="flex-1 md:pl-4">
                    <div className="front pt-3 md:pt-4">Allergies</div>
                    <span className="brief">{clinical.allergies}</span>
                  </div>
                </div>
              </div>

              <div className="md:pb-4">
                <div className="md:flex justify-content-between">
                  <div className="flex-1">
                    <div className="front pt-3 md:pt-4">Medical History</div>
                    <span className="brief">{clinical.medical_history}</span>
                  </div>
                  <div className="flex-1 md:pl-4">
                    <div className="front pt-3 md:pt-4">Family History</div>
                    <span className="brief">{clinical.family_history}</span>
                  </div>
                </div>
              </div>

              <div className="md:pb-4">
                <div className="md:flex justify-content-between">
                  <div className="flex-1">
                    <div className="front pt-3 md:pt-4">Procedures</div>
                    <span className="brief">{clinical.procedures}</span>
                  </div>
                  <div className="flex-1 md:pl-4">
                    <div className="front pt-3 md:pt-4">Immunizations</div>
                    <span className="brief">{clinical.immunizations}</span>
                  </div>
                </div>
              </div>

              <div className="md:pb-4">
                <div className="md:flex justify-content-between">
                  <div className="flex-1">
                    <div className="front pt-3 md:pt-4">Medications</div>
                    <span className="brief">{clinical.medications}</span>
                  </div>
                </div>
              </div>
            </>
          );
        })()}

        {(() => {
          const [leftEye14th] = fourteenthDayVisit?.patientvisiteyeexam?.filter(item => item?.eye_type === "Left Eye") ?? []
          const [rightEye14th] = fourteenthDayVisit?.patientvisiteyeexam?.filter(item => item?.eye_type === "Right Eye") ?? []
          const examDetails14th = getExaminationDetailsForVisit(fourteenthDayVisit);
          return (
            <>
              <div className="dry-refraction-container">
                {/* Title */}
                <div className="front main-label pt-3">Dry Refraction</div>
              </div>

              {/* Tables Container */}
              <div className="tables-container mb-3">
                <div className="flex flex-column w-full">
                  <div className="left-vision main-label text-center">L/DO</div>
                  <div className="table-section">
                    <table>
                      <thead className="brief">
                        <tr className="brief">
                          <th className="main-label-sec font-normal font-13px">Type</th>
                          <th className="main-label-sec font-normal font-13px">Sph</th>
                          <th className="main-label-sec font-normal font-13px">Ne.</th>
                          <th className="main-label-sec font-normal font-13px">Axis</th>
                          <th className="main-label-sec font-normal font-13px">Vis</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="brief main-label-sec">Distance</td>
                          <td>{leftEye14th?.distance_sph ?? "-"}</td>
                          <td>{leftEye14th?.distance_necylar ?? "-"}</td>
                          <td>{leftEye14th?.distance_axis ?? "-"}</td>
                          <td>{leftEye14th?.distance_vision ?? "-"}</td>
                        </tr>
                        <tr>
                          <td className="brief main-label-sec">Near</td>
                          <td>{leftEye14th?.near_sph ?? "-"}</td>
                          <td>{leftEye14th?.near_necylar ?? "-"}</td>
                          <td>{leftEye14th?.near_axis ?? "-"}</td>
                          <td>{leftEye14th?.near_vision ?? "-"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Divider */}
                <div className="vertical-line"></div>

                <div className="flex mt-5 md:mt-0 flex-column w-full">
                  <div className="text-center main-label left-vision">R/DO</div>
                  <div className="table-section">
                    <table>
                      <thead>
                        <tr className="main-label-sec">
                          <th className="main-label-sec font-normal font-13px">Type</th>
                          <th className="main-label-sec font-normal font-13px">Sph</th>
                          <th className="main-label-sec font-normal font-13px">Ne.</th>
                          <th className="main-label-sec font-normal font-13px">Axis</th>
                          <th className="main-label-sec font-normal font-13px">Vis</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="brief main-label-sec">Distance</td>
                          <td>{rightEye14th?.distance_sph ?? "-"}</td>
                          <td>{rightEye14th?.distance_necylar ?? "-"}</td>
                          <td>{rightEye14th?.distance_axis ?? "-"}</td>
                          <td>{rightEye14th?.distance_vision ?? "-"}</td>
                        </tr>
                        <tr>
                          <td className="brief main-label-sec">Near</td>
                          <td>{rightEye14th?.near_sph ?? "-"}</td>
                          <td>{rightEye14th?.near_necylar ?? "-"}</td>
                          <td>{rightEye14th?.near_axis ?? "-"}</td>
                          <td>{rightEye14th?.near_vision ?? "-"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="md:pt-3"></div>
              {/* Eye Examination Details Section */}
              <div className="eye-examination-details">
                <div className="front main-label pt-3 text-center pb-2">Eye Examination Details</div>

                <div className="tables-container">
                  {/* L/DO Table */}
                  <div className="table-container">
                    <div className="front main-label pt-3 text-center">L/DO</div>
                    <div className="exam-table p-1rem">
                      <div>
                        {wrapList(examDetails14th).map((item, index) => (
                          <div className="" key={index}>
                            <div className="flex justify-content-between">
                              {item?.map((list, ind) => (
                                <>
                                  {ind === 1 && <div className="divider my-2" />}
                                  <div className="flex flex-column flex-1 pl-20px p-2">
                                    <div className="main-label Outline front">{list.label}</div>
                                    <div className="Benefit main-label front">{list.LDO}</div>
                                  </div>
                                </>))}
                            </div>
                            <div className="Boundary"></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="standing-stroke"></div>

                  {/* R/DO Table */}
                  <div className="table-container">
                    <div className="front main-label pt-3 text-center">R/DO</div>
                    <table className="exam-table p-1rem">
                      <div>
                        {wrapList(examDetails14th).map((item, index) => (
                          <div className="" key={index}>
                            <div className="flex justify-content-between">
                              {item?.map((list, ind) => (
                                <>
                                  {ind === 1 && <div className="divider my-2" />}
                                  <div className="flex flex-column flex-1 pl-20px p-2">
                                    <div className="Outline main-label front">{list.label}</div>
                                    <div className="Benefit main-label front">{list.RDO}</div>
                                  </div>
                                </>))}
                            </div>
                            <div className="Boundary"></div>
                          </div>
                        ))}
                      </div>
                    </table>
                  </div>
                </div>
              </div>
            </>
          );
        })()}

        {renderMedicalPrescriptionSection(fourteenthDayVisit)}

        {(() => {
          const clinical = getClinicalData(fortiethDayVisit);
          const [leftEye40th] = fortiethDayVisit?.patientvisiteyeexam?.filter(item => item?.eye_type === "Left Eye") ?? []
          const [rightEye40th] = fortiethDayVisit?.patientvisiteyeexam?.filter(item => item?.eye_type === "Right Eye") ?? []
          return (
            <>
              <div className="md:pt-4">
                <div className="heading">40th Day</div>
                <div className="parameter">Patient Vitals</div>
                <div className="medical-row pt-2 pb-4">
                  <div className="vital-item">
                    <div className="mark">Age</div>
                    <div className="brief">{fortiethDayVisit?.age ?? "-"}</div>
                  </div>
                  <div className="vital-item text-center">
                    <div className="mark">Height</div>
                    <div className="brief">{fortiethDayVisit?.height ?? "-"}</div>
                  </div>
                  <div className="vital-item text-center">
                    <div className="mark">Weight</div>
                    <div className="brief">{fortiethDayVisit?.weight ?? "-"}</div>
                  </div>
                  <div className="vital-item md:text-center">
                    <div className="mark">pulse</div>
                    <div className="brief">{fortiethDayVisit?.pulse ? `${fortiethDayVisit.pulse} bpm` : "-"}</div>
                  </div>
                  <div className="vital-item ml-4 md:ml-0 full-row md:text-right">
                    <div className="mark">Blood Pressure</div>
                    <div className="brief">{fortiethDayVisit?.blood_pressure ? `${fortiethDayVisit.blood_pressure} mm Hg` : "-"}</div>
                  </div>
                </div>
              </div>

              <div className="md:pb-4">
                <div className="md:flex justify-content-between">
                  <div className="flex-1">
                    <div className="front pt-3 md:pt-4">Ophthalmic History</div>
                    <span className="brief">{clinical.ophthalmic_history}</span>
                  </div>
                  <div className="flex-1 md:pl-4">
                    <div className="front pt-3 md:pt-4">Allergies</div>
                    <span className="brief">{clinical.allergies}</span>
                  </div>
                </div>
              </div>

              <div className="md:pb-4">
                <div className="md:flex justify-content-between">
                  <div className="flex-1">
                    <div className="front pt-3 md:pt-4">Medical History</div>
                    <span className="brief">{clinical.medical_history}</span>
                  </div>
                  <div className="flex-1 md:pl-4">
                    <div className="front pt-3 md:pt-4">Family History</div>
                    <span className="brief">{clinical.family_history}</span>
                  </div>
                </div>
              </div>

              <div className="md:pb-4">
                <div className="md:flex justify-content-between">
                  <div className="flex-1">
                    <div className="front pt-3 md:pt-4">Procedures</div>
                    <span className="brief">{clinical.procedures}</span>
                  </div>
                  <div className="flex-1 md:pl-4">
                    <div className="front pt-3 md:pt-4">Immunizations</div>
                    <span className="brief">{clinical.immunizations}</span>
                  </div>
                </div>
              </div>

              <div className="md:pb-4">
                <div className="flex justify-content-between">
                  <div className="flex-1">
                    <div className="front pt-3 md:pt-4">Medications</div>
                    <span className="brief">{clinical.medications}</span>
                  </div>
                </div>
              </div>

              <div className="dry-refraction-container">
                {/* Title */}
                <div className="front main-label pt-3">Dry Refraction</div>
              </div>

              {/* Tables Container */}
              <div className="tables-container mb-3">
                <div className="flex flex-column w-full">
                  <div className="left-vision main-label text-center">L/DO</div>
                  <div className="table-section">
                    <table>
                      <thead className="brief">
                        <tr className="brief">
                          <th className="main-label-sec font-normal font-13px">Type</th>
                          <th className="main-label-sec font-normal font-13px">Sph</th>
                          <th className="main-label-sec font-normal font-13px">Ne.</th>
                          <th className="main-label-sec font-normal font-13px">Axis</th>
                          <th className="main-label-sec font-normal font-13px">Vis</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="brief main-label-sec">Distance</td>
                          <td>{leftEye40th?.distance_sph ?? "-"}</td>
                          <td>{leftEye40th?.distance_necylar ?? "-"}</td>
                          <td>{leftEye40th?.distance_axis ?? "-"}</td>
                          <td>{leftEye40th?.distance_vision ?? "-"}</td>
                        </tr>
                        <tr>
                          <td className="brief main-label-sec">Near</td>
                          <td>{leftEye40th?.near_sph ?? "-"}</td>
                          <td>{leftEye40th?.near_necylar ?? "-"}</td>
                          <td>{leftEye40th?.near_axis ?? "-"}</td>
                          <td>{leftEye40th?.near_vision ?? "-"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Divider */}
                <div className="vertical-line"></div>

                <div className="flex flex-column w-full mt-5 md:mt-0 ">
                  <div className="text-center main-label left-vision ">R/DO</div>
                  <div className="table-section">
                    <table>
                      <thead>
                        <tr className="main-label-sec">
                          <th className="main-label-sec font-normal font-13px">Type</th>
                          <th className="main-label-sec font-normal font-13px">Sph</th>
                          <th className="main-label-sec font-normal font-13px">Ne.</th>
                          <th className="main-label-sec font-normal font-13px">Axis</th>
                          <th className="main-label-sec font-normal font-13px">Vis</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="brief main-label-sec">Distance</td>
                          <td>{rightEye40th?.distance_sph ?? "-"}</td>
                          <td>{rightEye40th?.distance_necylar ?? "-"}</td>
                          <td>{rightEye40th?.distance_axis ?? "-"}</td>
                          <td>{rightEye40th?.distance_vision ?? "-"}</td>
                        </tr>
                        <tr>
                          <td className="brief main-label-sec">Near</td>
                          <td>{rightEye40th?.near_sph ?? "-"}</td>
                          <td>{rightEye40th?.near_necylar ?? "-"}</td>
                          <td>{rightEye40th?.near_axis ?? "-"}</td>
                          <td>{rightEye40th?.near_vision ?? "-"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          );
        })()}

        {(() => {
          const examDetails40th = getExaminationDetailsForVisit(fortiethDayVisit);
          return (
            <>
              <div className="md:pt-3"></div>
              {/* Eye Examination Details Section */}
              <div className="eye-examination-details">
                <div className="front main-label pt-3 text-center pb-2">Eye Examination Details</div>

                <div className="tables-container">
                  {/* L/DO Table */}
                  <div className="table-container">
                    <div className="front main-label pt-3 text-center">L/DO</div>
                    <div className="exam-table p-1rem">
                      <div>
                        {wrapList(examDetails40th).map((item, index) => (
                          <div className="" key={index}>
                            <div className="flex justify-content-between">
                              {item?.map((list, ind) => (
                                <>
                                  {ind === 1 && <div className="divider my-2" />}
                                  <div className="flex flex-column flex-1 pl-20px p-2">
                                    <div className="main-label Outline front">{list.label}</div>
                                    <div className="Benefit main-label front">{list.LDO}</div>
                                  </div>
                                </>))}
                            </div>
                            <div className="Boundary"></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="standing-stroke"></div>

                  {/* R/DO Table */}
                  <div className="table-container">
                    <div className="front main-label pt-3 text-center">R/DO</div>
                    <table className="exam-table p-1rem">
                      <div>
                        {wrapList(examDetails40th).map((item, index) => (
                          <div className="" key={index}>
                            <div className="flex justify-content-between">
                              {item?.map((list, ind) => (
                                <>
                                  {ind === 1 && <div className="divider my-2" />}
                                  <div className="flex flex-column flex-1 pl-20px p-2">
                                    <div className="Outline main-label front">{list.label}</div>
                                    <div className="Benefit main-label front">{list.RDO}</div>
                                  </div>
                                </>))}
                            </div>
                            <div className="Boundary"></div>
                          </div>
                        ))}
                      </div>
                    </table>
                  </div>
                </div>
              </div>
            </>
          );
        })()}

        {renderMedicalPrescriptionSection(fortiethDayVisit)}

      </div>
    </div>
  );
}
